// ══════════════════════════════════════════════════════════════════════════════════════════════
//  ENTRAR CON FACE ID, TOUCH ID O LA HUELLA — LO QUE HACE EL MÓVIL  (3-oct-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «introducir el acceso biométrico en iPhone y en Android [...] es un incordio tener que ponerla cada vez».
//
//  SOLO DENTRO DE LAS APPS (Capacitor) Y SOLO SI LA APP COMPILADA LLEVA EL COMPLEMENTO. Las apps cargan la web en vivo:
//  esta misma página corre en el navegador del ordenador (donde no hay nada que ofrecer) y en apps del móvil compiladas
//  ANTES del complemento (donde llamarlo fallaría). `Capacitor.isPluginAvailable('NativeBiometric')` solo es verdad en
//  una app compilada con @capgo/capacitor-native-biometric dentro: en lo demás, no sale nada y todo sigue como siempre.
//
//  DÓNDE SE GUARDA LA LLAVE: en el llavero seguro del sistema (Keychain del iPhone, Keystore de Android), protegida por
//  la biometría (`accessControl` BIOMETRY_CURRENT_SET): para leerla hay que poner la cara o el dedo, y si alguien añade
//  una cara o una huella nueva al móvil, la llave se borra sola y hay que volver a activarla con la contraseña.
//  NADA en localStorage: allí solo va la preferencia «no me lo vuelvas a preguntar» (que no abre nada).
//  Lo que se guarda es una LLAVE DE ESTE MÓVIL que da el servidor (routes/entradaBiometrica.js para el equipo,
//  routes/portalBiometria.js para el paciente). NUNCA la contraseña.
//
//  Al lado, SIN biometría, se guarda la parte PÚBLICA de cada llave (no abre nada): sirve para saber que está activada
//  sin pedir la cara, y para apagarla en el servidor al quitarla de este móvil.
import { Capacitor, registerPlugin } from '@capacitor/core'
// t169b (12-oct-2026) · una hoja de Face ID a la vez (utils/faceIdUnaVez): mientras hay una abierta, otra no sale ({ enCurso }).
import { conLaHoja } from '../utils/faceIdUnaVez'
import { t as i18nT } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// El complemento nativo, por su nombre. Sin implementación web a propósito: en el navegador no se llama nunca.
let _plugin = null
function complemento() {
  if (!_plugin) _plugin = registerPlugin('NativeBiometric')
  return _plugin
}

const CLAVE = { equipo: 'biango.entrada.equipo', paciente: 'biango.entrada.paciente' }
const CLAVE_PUBLICA = { equipo: 'biango.entrada.equipo.publica', paciente: 'biango.entrada.paciente.publica' }
const NO_PREGUNTAR = { equipo: 'biango_biometria_no_preguntar', paciente: 'biango_biometria_no_preguntar_portal' }
// AccessControl.BIOMETRY_CURRENT_SET del complemento: solo con la biometría de hoy; una cara o huella nueva la borra.
const SOLO_ESTA_BIOMETRIA = 1
// Lo que contesta el complemento cuando la persona cancela o elige la contraseña: no es un error que haya que contar.
const CANCELADO = new Set([10, 11, 13, 15, 16, 17])
const SIN_LLAVE = 21

// ¿Corre esto dentro de una app del móvil que lleva el complemento?
export function hayBiometriaEnEstaApp(cap = Capacitor) {
  try { return !!(cap && cap.isNativePlatform() && cap.isPluginAvailable('NativeBiometric')) } catch { return false }
}

// Cómo se llama lo que tiene ESTE móvil, para los textos («Entrar con Face ID»).
export function nombreBiometria(tipo) {
  switch (Number(tipo)) {
    case 1: return 'Touch ID'
    case 2: return 'Face ID'
    case 3: return 'la huella'
    case 4: return 'la cara'
    case 5: return 'el iris'
    case 6: return i18nT('services.entradaBiometrica.la_huella_o_la_cara', 'la huella o la cara')
    default: return i18nT('services.entradaBiometrica.la_huella_o_la_cara', 'la huella o la cara')
  }
}

/**
 * Lo que hay en ESTE móvil. Nunca lanza.
 * { app: lleva el complemento, puede: tiene biometría puesta, nombre: 'Face ID'…, activada: hay llave guardada }
 */
export async function estadoEnEsteMovil(cual) {
  const vacio = { app: false, puede: false, nombre: i18nT('services.entradaBiometrica.la_huella_o_la_cara', 'la huella o la cara'), activada: false }
  if (!hayBiometriaEnEstaApp()) return vacio
  const out = { ...vacio, app: true }
  try {
    const r = await complemento().isAvailable({ useFallback: false })
    out.puede = !!(r && r.isAvailable)
    out.nombre = nombreBiometria(r && r.biometryType)
  } catch { /* sin biometría */ }
  try {
    const g = await complemento().isDataSaved({ key: CLAVE[cual] })
    out.activada = !!(g && g.isSaved)
  } catch { /* nada guardado */ }
  return out
}

// Guarda las llaves que acaba de dar el servidor. En Android, proteger la llave ya pide la huella una vez.
export async function guardarLlaves(cual, { tokens, publicos }) {
  await complemento().setData({
    key: CLAVE[cual], value: JSON.stringify({ v: 1, tokens: (tokens || []).slice(0, 12) }),
    accessControl: SOLO_ESTA_BIOMETRIA, title: i18nT('services.entradaBiometrica.proteger_la_entrada', 'Proteger la entrada'), negativeButtonText: 'Cancelar',
  })
  try {
    await complemento().setData({ key: CLAVE_PUBLICA[cual], value: JSON.stringify({ v: 1, publicos: (publicos || []).slice(0, 12) }) })
  } catch { /* sin la parte pública solo se pierde poder apagarla en el servidor desde aquí */ }
}

/**
 * Pide la cara o la huella y devuelve las llaves. { tokens } · { cancelado } · { sinLlave } · { error }
 */
export function leerLlaves(cual, opciones = {}) { return conLaHoja(() => leerLlavesYa(cual, opciones)) }
async function leerLlavesYa(cual, { motivo, titulo } = {}) {
  try {
    const r = await complemento().getSecureData({
      key: CLAVE[cual], reason: motivo || i18nT('services.entradaBiometrica.entrar_sin_escribir_la_contrasena', 'Entrar sin escribir la contraseña'),
      title: titulo || i18nT('services.entradaBiometrica.entrar', 'Entrar'), negativeButtonText: i18nT('services.entradaBiometrica.usar_la_contrasena', 'Usar la contraseña'), fallbackTitle: '',
    })
    const d = JSON.parse((r && r.value) || '{}')
    const tokens = Array.isArray(d.tokens) ? d.tokens.filter((t) => typeof t === 'string') : []
    return tokens.length ? { tokens } : { sinLlave: true }
  } catch (e) {
    const code = Number(e && e.code)
    if (code === SIN_LLAVE) return { sinLlave: true }
    if (CANCELADO.has(code)) return { cancelado: true }
    return { error: (e && e.message) || i18nT('services.entradaBiometrica.no_se_ha_podido_leer', 'No se ha podido leer la llave') }
  }
}

// Quita la llave de ESTE móvil (no pide la cara: borrar no la necesita) y devuelve la parte pública para apagarla en
// el servidor.
export async function borrarLlaves(cual) {
  let publicos = []
  try {
    const p = await complemento().getData({ key: CLAVE_PUBLICA[cual] })
    const d = JSON.parse((p && p.value) || '{}')
    publicos = Array.isArray(d.publicos) ? d.publicos : []
  } catch { /* no había */ }
  try { await complemento().deleteData({ key: CLAVE[cual] }) } catch { /* ya no estaba */ }
  try { await complemento().deleteData({ key: CLAVE_PUBLICA[cual] }) } catch { /* ya no estaba */ }
  return publicos
}

// La parte pública que guarda ESTE móvil (para marcar «este móvil» en la lista). Nunca lanza.
export async function publicasDeEsteMovil(cual) {
  if (!hayBiometriaEnEstaApp()) return []
  try {
    const p = await complemento().getData({ key: CLAVE_PUBLICA[cual] })
    const d = JSON.parse((p && p.value) || '{}')
    return Array.isArray(d.publicos) ? d.publicos : []
  } catch { return [] }
}

// «No me lo vuelvas a preguntar» en este móvil: solo una preferencia, no abre nada.
// 5-oct-2026 · «AHORA NO» YA NO ES PARA SIEMPRE: el dueño quiere que se le ofrezca a quien todavía no lo tiene, «una vez,
// sin agobiar». Se guarda CUÁNDO se dijo que no y se vuelve a ofrecer pasados DIAS_SIN_PREGUNTAR (lo de antes, un «1»
// sin fecha, cuenta desde la primera vez que se mira).
export const DIAS_SIN_PREGUNTAR = 30
export function noPreguntar(cual, ahora = Date.now()) {
  try {
    const v = localStorage.getItem(NO_PREGUNTAR[cual])
    if (!v) return false
    let cuando = Number(v)
    if (!(cuando > 1000000000000)) { cuando = ahora; marcarNoPreguntar(cual, true, ahora) }
    return ahora - cuando < DIAS_SIN_PREGUNTAR * 24 * 3600 * 1000
  } catch { return false }
}
export function marcarNoPreguntar(cual, si = true, ahora = Date.now()) {
  try { if (si) localStorage.setItem(NO_PREGUNTAR[cual], String(ahora)); else localStorage.removeItem(NO_PREGUNTAR[cual]) } catch { /* sin almacenamiento */ }
}

// Al abrir la app se pide la cara UNA vez por arranque; al salir de la sesión no se vuelve a pedir sola (sale el botón).
const PEDIDA = 'biango_biometria_pedida'
export function yaSePidioAlAbrir() {
  try { return sessionStorage.getItem(PEDIDA) === '1' } catch { return true }
}
export function apuntarPedidaAlAbrir() {
  try { sessionStorage.setItem(PEDIDA, '1') } catch { /* sin almacenamiento */ }
}

// Nombre del móvil para la lista («iPhone», «Android»).
export function plataforma() {
  try { return Capacitor.getPlatform() } catch { return null }
}
